import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, BellRing, PauseCircle, Gauge, Clock, TrendingUp, Mail, Banknote } from "lucide-react";
import { toast } from "sonner";

const MERCHANT_ROWS = [
  { key: "near_limit", icon: Gauge, title: "Near daily limit", desc: "Alert when a UPI ID approaches its daily cap." },
  { key: "limit_reached", icon: TrendingUp, title: "Daily limit reached", desc: "Alert when a UPI ID hits its daily cap." },
  { key: "stalled", icon: Clock, title: "Stopped receiving credits", desc: "Alert when an active UPI ID goes quiet for 6h+." },
  { key: "auto_pause", icon: PauseCircle, title: "Auto-pause unhealthy UPI IDs", desc: "Temporarily remove limit-reached / stalled IDs from rotation and retry automatically." },
  { key: "email_alerts", icon: Mail, title: "Email me these alerts", desc: "Also send health & auto-pause alerts to your email, even when you're logged out." },
];

const ADMIN_ROWS = [
  { key: "email_alerts", icon: Banknote, title: "Email me settlement reminders", desc: "Email the daily reminder about approved payouts still awaiting disbursement, alongside the in-app alert." },
];

export default function Preferences() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const ROWS = isAdmin ? ADMIN_ROWS : MERCHANT_ROWS;
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/preferences").then((r) => setPrefs(r.data)).catch(() => setPrefs(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = isAdmin ? { email_alerts: prefs.email_alerts } : prefs;
      const { data } = await api.put("/preferences", payload);
      setPrefs(data);
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight">Alert Preferences</h2>
        <p className="text-sm text-muted-foreground">
          {isAdmin
            ? "Choose which platform alerts land in your inbox."
            : "Choose which health alerts you receive and tune the thresholds."}
        </p>
      </div>

      <Card className="p-2">
        {ROWS.map((r, i) => (
          <div key={r.key} className={`flex items-center gap-4 p-4 ${i < ROWS.length - 1 ? "border-b" : ""}`}>
            <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
              <r.icon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{r.title}</p>
              <p className="text-xs text-muted-foreground">{r.desc}</p>
            </div>
            <Switch
              checked={!!prefs[r.key]}
              onCheckedChange={(v) => setPrefs({ ...prefs, [r.key]: v })}
              data-testid={`pref-${r.key}`}
            />
          </div>
        ))}
      </Card>

      {!isAdmin && (
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <BellRing className="h-4 w-4 text-primary" />
            <h3 className="font-heading font-semibold">Near-limit threshold</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-3">Trigger the "near limit" alert once a UPI ID crosses this percentage of its daily cap.</p>
          <div className="flex items-center gap-3 max-w-xs">
            <Input
              type="number" min="50" max="99"
              value={prefs.near_limit_threshold}
              onChange={(e) => setPrefs({ ...prefs, near_limit_threshold: e.target.value })}
              data-testid="pref-threshold-input"
            />
            <span className="text-sm text-muted-foreground">% of daily limit</span>
          </div>
        </Card>
      )}

      <Button onClick={save} disabled={saving} data-testid="pref-save-button">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save preferences</>}
      </Button>
    </div>
  );
}
